/**
 * ExportCustomersPanel — Feb 2026
 * ------------------------------------------------------------------
 * Admin-side CRUD for the Export Customers master registry. Renders
 * as the "Export Customers" tab inside the Admin → Customers page.
 *
 * Data flow
 *   • Loads via `GET /api/admin/export-customers?q=…`
 *   • Creates via `POST /api/admin/export-customers`
 *   • Updates via `PUT  /api/admin/export-customers/:id`
 *   • Deletes via `DELETE /api/admin/export-customers/:id` (blocked
 *     server-side if any order still references the record).
 *
 * Every master gets an auto-generated `EXP-000001` customer number
 * used by the New Export Order modal for quick lookup.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Search, Plus, Loader2, Save, X, Pencil, Trash2, Globe2, Copy, UserCheck, DollarSign } from 'lucide-react';
import { Input } from './ui/input';
import CustomerNameLabel from './CustomerNameLabel';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { useAdminAuth } from '../context/AdminAuthContext';
import { EXPORT_COUNTRIES, findCountryByCode } from '../data/countries';
import ExportCustomerManualPricesDialog from './ExportCustomerManualPricesDialog';

const EMPTY_DRAFT = {
  name: '',
  business_name: '',
  email: '',
  address: '',
  country_code: '',
  phone_dial_code: '',
  phone: '',
};

const ExportCustomersPanel = ({ API, authHeaders }) => {
  const { toast } = useToast();
  const { isSuperAdmin } = useAdminAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(EMPTY_DRAFT);
  const [busyId, setBusyId] = useState('');
  // Aug 2026 — Manual Prices dialog. Fetches the full product list
  // once (lazily) so the dialog's product picker can filter locally
  // without hammering the backend on each keystroke.
  const [manualPricesFor, setManualPricesFor] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = q ? { q } : {};
      const { data } = await axios.get(`${API}/admin/export-customers`, { headers: authHeaders, params });
      setRows(data?.customers || []);
    } catch (err) {
      toast({ title: 'Load failed', description: err?.response?.data?.detail || err?.message });
    } finally { setLoading(false); }
  }, [API, authHeaders, q, toast]);

  useEffect(() => { load(); }, [load]);

  const onDraftCountryChange = (setter, current) => (code) => {
    const meta = findCountryByCode(code);
    setter({
      ...current,
      country_code: code,
      phone_dial_code: meta?.dial || current.phone_dial_code || '',
      phone: current.phone && current.phone.startsWith('+') && !current.phone.startsWith(meta?.dial || '__')
        ? `${meta?.dial || ''} `
        : (current.phone || (meta ? `${meta.dial} ` : '')),
    });
  };

  const create = async () => {
    const name = (draft.name || '').trim();
    const business_name = (draft.business_name || '').trim();
    const email = (draft.email || '').trim().toLowerCase();
    if (!business_name) { toast({ title: 'Business name is required' }); return; }
    if (!name) { toast({ title: 'Contact name is required' }); return; }
    // Email is optional (Feb 2026). If the caller typed one, validate
    // the shape; otherwise skip and let the backend accept a phone-only
    // master.
    if (email && !email.includes('@')) { toast({ title: 'Enter a valid email or leave it blank' }); return; }
    setSaving(true);
    try {
      const meta = findCountryByCode(draft.country_code);
      const body = {
        name,
        business_name,
        address: (draft.address || '').trim(),
        country_code: (draft.country_code || '').trim().toUpperCase(),
        country_name: meta?.name || '',
        phone_dial_code: (draft.phone_dial_code || meta?.dial || '').trim(),
        phone: (draft.phone || '').trim(),
      };
      if (email) body.email = email;
      const { data } = await axios.post(`${API}/admin/export-customers`, body, { headers: authHeaders });
      toast({ title: 'Export customer added', description: `${data.customer_number} · ${data.business_name || data.name}` });
      setAdding(false);
      setDraft(EMPTY_DRAFT);
      load();
    } catch (err) {
      toast({ title: 'Create failed', description: err?.response?.data?.detail || err?.message });
    } finally { setSaving(false); }
  };

  const beginEdit = (row) => {
    setEditingId(row.id);
    setEditDraft({
      name: row.name || '',
      business_name: row.business_name || '',
      email: row.email || '',
      address: row.address || '',
      country_code: row.country_code || '',
      phone_dial_code: row.phone_dial_code || '',
      phone: row.phone || '',
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setBusyId(editingId);
    try {
      const meta = findCountryByCode(editDraft.country_code);
      const body = {
        name: (editDraft.name || '').trim(),
        business_name: (editDraft.business_name || '').trim(),
        address: (editDraft.address || '').trim(),
        country_code: (editDraft.country_code || '').trim().toUpperCase(),
        country_name: meta?.name || '',
        phone_dial_code: (editDraft.phone_dial_code || meta?.dial || '').trim(),
        phone: (editDraft.phone || '').trim(),
      };
      // Super admin can override the email (break-glass on legacy typos).
      // Backend silently ignores the field for sub-admins.
      if (isSuperAdmin) {
        body.email = (editDraft.email || '').trim().toLowerCase();
      }
      await axios.put(`${API}/admin/export-customers/${editingId}`, body, { headers: authHeaders });
      toast({ title: 'Updated' });
      setEditingId(null);
      setEditDraft(EMPTY_DRAFT);
      load();
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || err?.message });
    } finally { setBusyId(''); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.customer_number} (${row.name})?\n\nBlocked if any order still references this customer.`)) return;
    setBusyId(row.id);
    try {
      await axios.delete(`${API}/admin/export-customers/${row.id}`, { headers: authHeaders });
      toast({ title: 'Deleted', description: `${row.customer_number} removed` });
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || err?.message });
    } finally { setBusyId(''); }
  };

  const copyNumber = (cnum) => {
    try {
      navigator.clipboard.writeText(cnum);
      toast({ title: 'Copied', description: cnum });
    } catch { /* ignore */ }
  };

  // Aug 2026 — Lazy-load products the first time the Manual Prices
  // dialog is opened. Keeps the tab snappy for the common case where
  // no one clicks the button.
  const openManualPrices = async (row) => {
    if (!productsLoaded) {
      try {
        const { data } = await axios.get(`${API}/admin/products`, { headers: authHeaders });
        const list = Array.isArray(data) ? data : (data?.products || []);
        setProducts(list);
        setProductsLoaded(true);
      } catch (err) {
        toast({
          title: 'Could not load products',
          description: err?.response?.data?.detail || err?.message,
        });
      }
    }
    setManualPricesFor(row);
  };

  const filtered = useMemo(() => rows, [rows]);

  return (
    <div className="space-y-4" data-testid="export-customers-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by EXP number, name, business, or email"
              className="pl-9"
              data-testid="export-customers-search"
            />
          </div>
          <span className="text-xs text-neutral-500" data-testid="export-customers-count">
            {loading ? '…' : `${filtered.length} customer${filtered.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <Button
          onClick={() => { setDraft(EMPTY_DRAFT); setAdding(true); }}
          className="bg-[#0078D4] hover:bg-[#005A9E] text-white"
          data-testid="export-customer-add-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Add export customer
        </Button>
      </div>

      {adding && (
        <div className="bg-white rounded-2xl border border-[#0078D4]/30 shadow-sm p-5" data-testid="export-customer-add-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-[#0F172A] flex items-center gap-2">
              <Globe2 className="w-4 h-4 text-[#0078D4]" /> New export customer
            </h3>
            <button
              type="button"
              onClick={() => { setAdding(false); setDraft(EMPTY_DRAFT); }}
              className="text-neutral-400 hover:text-neutral-700 p-1"
              aria-label="Close"
              data-testid="export-customer-add-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-neutral-500 mb-4">
            A unique <span className="font-mono font-bold">EXP-XXXXXX</span> customer number will be auto-assigned. Historic export orders with the same email will be back-linked automatically.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Feb 2026 — Business name is the FIRST field and MANDATORY.
                Ops care more about the company (which prints on the
                invoice + email) than the individual contact name. */}
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Business name *</label>
              <Input value={draft.business_name} onChange={(e) => setDraft({ ...draft, business_name: e.target.value })} placeholder="Acme Trading LLC" className="mt-1" data-testid="export-customer-business" required />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Contact name *</label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="John Doe" className="mt-1" data-testid="export-customer-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Email</label>
              <Input type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="name@example.com (optional)" className="mt-1" data-testid="export-customer-email" />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Country</label>
              <select
                value={draft.country_code}
                onChange={(e) => onDraftCountryChange(setDraft, draft)(e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-neutral-200 px-3 text-sm bg-white focus:outline-none focus:border-[#0078D4]"
                data-testid="export-customer-country"
              >
                <option value="">— Pick a country —</option>
                {EXPORT_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name} ({c.dial})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Phone</label>
              <Input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder={findCountryByCode(draft.country_code)?.sample || '+971 50 123 4567'} className="mt-1 font-mono" data-testid="export-customer-phone" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Address</label>
              <Input value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} placeholder="Street, City, Country" className="mt-1" data-testid="export-customer-address" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <Button
              variant="outline"
              onClick={() => { setAdding(false); setDraft(EMPTY_DRAFT); }}
              data-testid="export-customer-add-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={create}
              disabled={saving}
              className="bg-[#0078D4] hover:bg-[#005A9E] text-white"
              data-testid="export-customer-add-save"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-10 text-center text-neutral-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-neutral-200 p-12 text-center" data-testid="export-customers-empty">
          <div className="inline-flex w-14 h-14 rounded-full bg-amber-50 items-center justify-center mb-4">
            <Globe2 className="w-6 h-6 text-amber-600" />
          </div>
          <h3 className="text-lg font-bold text-[#0F172A]">
            {q ? 'No matches' : 'No export customers yet'}
          </h3>
          <p className="text-sm text-neutral-500 mt-1 max-w-md mx-auto">
            {q ? 'Try a different search term or clear the filter.' : 'Add an international buyer to reuse their details on every future export order — they\'ll be searchable by their EXP number.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-neutral-200 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
                <th className="p-3">Customer #</th>
                <th className="p-3">Business</th>
                <th className="p-3">Contact</th>
                <th className="p-3">Email</th>
                <th className="p-3">Country</th>
                <th className="p-3">Phone</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.map((row) => {
                const isEditing = editingId === row.id;
                if (isEditing) {
                  return (
                    <tr key={row.id} className="bg-[#DEECF9]/30" data-testid={`export-customer-edit-row-${row.id}`}>
                      <td className="p-3 align-top">
                        <div className="font-mono font-bold text-[#0078D4]">{row.customer_number}</div>
                        <div className="text-[10px] text-neutral-400">{isSuperAdmin ? 'Email editable' : 'Email locked'}</div>
                      </td>
                      <td className="p-3 align-top">
                        <Input value={editDraft.business_name} onChange={(e) => setEditDraft({ ...editDraft, business_name: e.target.value })} className="h-8" data-testid={`export-customer-edit-business-${row.id}`} />
                      </td>
                      <td className="p-3 align-top">
                        <Input value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} className="h-8" data-testid={`export-customer-edit-name-${row.id}`} />
                      </td>
                      <td className="p-3 align-top">
                        {isSuperAdmin ? (
                          <Input
                            type="email"
                            value={editDraft.email}
                            onChange={(e) => setEditDraft({ ...editDraft, email: e.target.value })}
                            className="h-8"
                            placeholder="name@example.com"
                            data-testid={`export-customer-edit-email-${row.id}`}
                          />
                        ) : (
                          <span className="text-xs text-neutral-500 break-all">{row.email}</span>
                        )}
                      </td>
                      <td className="p-3 align-top">
                        <select
                          value={editDraft.country_code}
                          onChange={(e) => onDraftCountryChange(setEditDraft, editDraft)(e.target.value)}
                          className="h-8 rounded-md border border-neutral-200 px-2 text-xs bg-white w-full"
                          data-testid={`export-customer-edit-country-${row.id}`}
                        >
                          <option value="">—</option>
                          {EXPORT_COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-3 align-top">
                        <Input value={editDraft.phone} onChange={(e) => setEditDraft({ ...editDraft, phone: e.target.value })} className="h-8 font-mono" data-testid={`export-customer-edit-phone-${row.id}`} />
                      </td>
                      <td className="p-3 align-top">
                        <div className="flex gap-1 justify-end">
                          <Button
                            onClick={saveEdit}
                            disabled={busyId === row.id}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 rounded-full px-3 text-xs"
                            data-testid={`export-customer-edit-save-${row.id}`}
                          >
                            {busyId === row.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                            Save
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => { setEditingId(null); setEditDraft(EMPTY_DRAFT); }}
                            className="h-8 rounded-full px-3 text-xs"
                            data-testid={`export-customer-edit-cancel-${row.id}`}
                          >
                            Cancel
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                const meta = findCountryByCode(row.country_code);
                return (
                  <tr key={row.id} data-testid={`export-customer-row-${row.id}`}>
                    <td className="p-3">
                      <button
                        type="button"
                        onClick={() => copyNumber(row.customer_number)}
                        className="font-mono font-bold text-[#0078D4] inline-flex items-center gap-1 hover:underline"
                        title="Click to copy"
                        data-testid={`export-customer-copy-${row.id}`}
                      >
                        {row.customer_number}
                        <Copy className="w-3 h-3 opacity-60" />
                      </button>
                    </td>
                    <td className="p-3">
                      <CustomerNameLabel customer={row} testIdPrefix={`export-customer-${row.id}`} />
                    </td>
                    <td className="p-3 text-xs text-neutral-600 hidden md:table-cell">{row.name || '—'}</td>
                    <td className="p-3 text-xs text-neutral-600 break-all">{row.email}</td>
                    <td className="p-3 text-xs">
                      {row.country_code ? (
                        <span className="inline-flex items-center gap-1">
                          <Globe2 className="w-3 h-3 text-amber-600" />
                          <span className="font-mono uppercase">{row.country_code}</span>
                          <span className="text-neutral-500">{meta?.name || row.country_name || ''}</span>
                        </span>
                      ) : <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="p-3 text-xs font-mono text-neutral-600">{row.phone || '—'}</td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end flex-wrap">
                        <button
                          type="button"
                          onClick={() => openManualPrices(row)}
                          className="text-xs px-2 py-1 rounded-full bg-white border border-[#0078D4]/30 hover:bg-[#DEECF9]/40 text-[#0078D4] inline-flex items-center gap-1"
                          title="Set customer-specific product prices"
                          data-testid={`export-customer-manual-prices-btn-${row.id}`}
                        >
                          <DollarSign className="w-3 h-3" /> Manual Prices
                        </button>
                        <button
                          type="button"
                          onClick={() => beginEdit(row)}
                          className="text-xs px-2 py-1 rounded-full bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-700 inline-flex items-center gap-1"
                          data-testid={`export-customer-edit-btn-${row.id}`}
                        >
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(row)}
                          disabled={busyId === row.id}
                          className="text-xs px-2 py-1 rounded-full bg-white border border-red-200 hover:bg-red-50 text-red-700 inline-flex items-center gap-1 disabled:opacity-40"
                          data-testid={`export-customer-delete-btn-${row.id}`}
                        >
                          {busyId === row.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {manualPricesFor && (
        <ExportCustomerManualPricesDialog
          API={API}
          authHeaders={authHeaders}
          customer={manualPricesFor}
          products={products}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setManualPricesFor(null)}
        />
      )}
    </div>
  );
};

export default ExportCustomersPanel;
export { UserCheck };
